import { IntegrationConfig } from "../types"

// Logos
import keheLogo from "/img/integration/kehe.png"
import unfiLogo from "/img/integration/unfi.png"
import targetLogo from "/img/integration/target.png"
import walmartLogo from "/img/integration/walmart.png"
import hyveeLogo from "/img/integration/hyvee.png"
import amazonLogo from "/img/integration/amazon.png"
import aholdLogo from "/img/integration/ahold.png"
import krogerLogo from "/img/integration/kroger.png"
import bozzutosLogo from "/img/integration/bozzutos.png"
import dotFoodsLogo from "/img/integration/dot.png"
import rainforestLogo from "/img/integration/rainforest.png"
import chexfinerfoodsLogo from "/img/integration/chexfinerfoods.png"
// Vector marks stay crisp and keep transparency instead of being flattened onto white like the
// rasterized logos above. GFI supplied theirs as a single-path SVG; Southern Glazer's is the
// official circular seal from southernglazers.com.
import gfiLogo from "/img/integration/gfi.svg"
import southernGlazerLogo from "/img/integration/southern-glazer.svg"
import vitaminShoppeLogo from "/img/integration/vitaminshoppe.png"
import gmailLogo from "/img/integration/gmail.png"
import microsoftLogo from "/img/integration/microsoft.png"

export const keheConfig: IntegrationConfig = {
  id: "kehe",
  name: "KeHE",
  category: "distributor",
  classOfTrade: "natural_specialty",
  logo: keheLogo,
  connectPath: "/setup/connect/kehe",
  settingsPath: "/settings/config/kehe",
  authType: "password",
}

export const unfiConfig: IntegrationConfig = {
  id: "unfi",
  name: "UNFI",
  category: "distributor",
  classOfTrade: "natural_specialty",
  logo: unfiLogo,
  connectPath: "/setup/connect/unfi",
  settingsPath: "/settings/config/unfi",
  authType: "email",
}

export const targetConfig: IntegrationConfig = {
  id: "target",
  name: "Target",
  category: "retailer",
  logo: targetLogo,
  description: "Reads Target's remittance emails, and its Partners Online payments, backup and Synergy disputes",
  connectPath: "/setup/connect/target",
  searchAliases: ["target", "partners online", "partnersonline", "pol", "synergy"],
  authType: "password",
}

export const walmartConfig: IntegrationConfig = {
  id: "walmart",
  name: "Walmart",
  category: "retailer",
  logo: walmartLogo,
  connectPath: "/setup/connect/walmart",
  authType: "password",
}

/** Walmart's second portal: its own login, on its own row (`walmart-highradius`) beside `walmart`. */
export const walmartHighRadiusConfig: IntegrationConfig = {
  id: "walmart-highradius",
  name: "Walmart HighRadius",
  category: "retailer",
  logo: walmartLogo,
  description: "What each Walmart bill is, its backup and its disputes (walmart.highradius.com).",
  searchAliases: ["highradius", "high radius", "walmart disputes", "sqep", "otif"],
  connectPath: "/setup/connect/walmart-highradius",
  settingsPath: "/settings/connect/walmart-highradius",
  authType: "password",
}

export const hyveeConfig: IntegrationConfig = {
  id: "hyvee",
  name: "Hy-Vee",
  category: "retailer",
  logo: hyveeLogo,
  connectPath: "/setup/connect/hyvee",
  searchAliases: ["hy-vee", "hyvee"],
  authType: "password",
}

export const amazonConfig: IntegrationConfig = {
  id: "amazon",
  name: "Amazon",
  category: "retailer",
  logo: amazonLogo,
  connectPath: "/setup/connect/amazon",
  authType: "password",
}

export const quickbooksConfig: IntegrationConfig = {
  id: "quickbooks",
  name: "Quickbooks",
  category: "accounting",
  logo: "/img/integration/quickbooks.png",
  connectPath: "/setup/connect/quickbooks",
  settingsPath: "/settings/config/quickbooks",
  authType: "oauth",
}

export const xeroConfig: IntegrationConfig = {
  id: "xero",
  name: "Xero",
  category: "accounting",
  logo: "/img/integration/xero.png",
  connectPath: "/setup/connect/xero",
  settingsPath: "/settings/config/xero",
  authType: "oauth",
}

export const sageIntacctConfig: IntegrationConfig = {
  id: "sageintacct",
  name: "Sage Intacct",
  category: "accounting",
  logo: "/img/integration/sageintacct.png",
  connectPath: "/setup/connect/sageintacct",
  settingsPath: "/settings/config/sageintacct",
  authType: "oauth",
}

export const netsuiteConfig: IntegrationConfig = {
  id: "netsuite",
  name: "NetSuite",
  category: "accounting",
  logo: "/img/integration/netsuite_logo.webp",
  connectPath: "/setup/connect/netsuite",
  authType: "oauth",
}

export const aholdConfig: IntegrationConfig = {
  id: "ahold",
  name: "Ahold",
  category: "retailer",
  logo: aholdLogo,
  connectPath: "/setup/connect/ahold",
  authType: "password",
}

export const svHarborConfig: IntegrationConfig = {
  id: "svharbor",
  name: "SV Harbor",
  category: "distributor",
  classOfTrade: "conventional",
  logo: "/img/integration/svharbor.png",
  connectPath: "/setup/connect/sv-harbor",
  authType: "password",
}

export const candsConfig: IntegrationConfig = {
  id: "cands",
  name: "C&S",
  category: "distributor",
  classOfTrade: "conventional",
  logo: "/img/integration/cands.png",
  connectPath: "/setup/connect/cands",
  settingsPath: "/settings/config/cands",
  searchAliases: ["c and s", "c & s", "c&s", "cands"],
  authType: "password",
}

export const spsConfig: IntegrationConfig = {
  id: "sps",
  name: "SPS Commerce",
  category: "logistics",
  logo: "/img/integration/sps.png",
  connectPath: "/setup/connect/sps",
  authType: "password",
}

export const bolEmailSyncConfig: IntegrationConfig = {
  id: "bol-email-sync",
  name: "Sync BOLs from email",
  category: "logistics",
  description: "Matches PDF attachments to deductions by PO",
  subLogos: [
    { src: gmailLogo, alt: "Gmail" },
    { src: microsoftLogo, alt: "Microsoft 365" },
  ],
  connectPath: "/setup/connect/bol-email-sync",
  searchAliases: ["bol", "bill of lading", "bol email", "email bol", "sync bol"],
  authType: "oauth",
}

// GFI's Mantis portal has no deductions area at all — every chargeback arrives as PDF backup on a
// recurring AP email — so this connects like BOL sync, over already-connected mailboxes rather than
// with its own credentials. It still gets its brand mark: the card is how a user finds the
// integration, and "Gourmet Foods International" over two mail icons reads like a mail feature
// rather than the distributor they are looking for.
export const gfiConfig: IntegrationConfig = {
  id: "gfi",
  name: "Gourmet Foods International",
  category: "distributor",
  // Specialty cheese and gourmet foods, mostly into grocery deli and specialty retail; restaurants
  // and food service are the smaller part.
  // Nat (10/12): brands look for it under Food service, which is how they buy from it.
  classOfTrade: "food_service",
  logo: gfiLogo,
  // A round stamp, not a wordmark: at the default h-10 it renders 40x40 and the lettering inside
  // it is unreadable next to KeHE's 117x40.
  logoHeightClass: "h-14",
  description: "Reads GFI's deduction backup emails",
  connectPath: "/setup/connect/gfi",
  settingsPath: "/settings/connect/gfi",
  searchAliases: ["gfi", "gourmet foods", "gourmet foods international", "mantis"],
  authType: "email",
}

export const krogerConfig: IntegrationConfig = {
  id: "kroger",
  name: "Kroger",
  category: "retailer",
  logo: krogerLogo,
  connectPath: "/setup/connect/kroger",
  settingsPath: "/settings/config/kroger",
  authType: "password",
}

export const bozzutosConfig: IntegrationConfig = {
  id: "bozzutos",
  name: "Bozzuto's",
  category: "distributor",
  classOfTrade: "conventional",
  logo: bozzutosLogo,
  connectPath: "/setup/connect/bozzutos",
  authType: "password",
}

export const dotFoodsConfig: IntegrationConfig = {
  id: "dotfoods",
  name: "Dot Foods",
  category: "distributor",
  // A redistributor: it sells to other distributors, food service first, with c-store, retail and
  // vending wholesalers behind it.
  classOfTrade: "redistributor",
  logo: dotFoodsLogo,
  connectPath: "/setup/connect/dotfoods",
  authType: "password",
}

export const dollarGeneralConfig: IntegrationConfig = {
  id: "dollargeneral",
  name: "Dollar General",
  logo: "/img/integration/dollargeneral.svg",
  category: "distributor",
  classOfTrade: "dollar",
  comingSoon: true,
}

export const crstlConfig: IntegrationConfig = {
  id: "crstl",
  name: "CRSTL",
  category: "logistics",
  logo: "/img/integration/crstl.png",
  connectPath: "/setup/connect/crstl",
  comingSoon: true,
}

// Freightzy is a freight partner building BOL/POD sync with us; listed as coming soon until that
// connection exists. Logo is the SVG from their own site header (freightzy.com).
export const freightzyConfig: IntegrationConfig = {
  id: "freightzy",
  name: "Freightzy",
  category: "logistics",
  logo: "/img/integration/freightzy.svg",
  description: "BOL and POD sync for Freightzy shipments",
  comingSoon: true,
}

export const stordConfig: IntegrationConfig = {
  id: "stord",
  name: "Stord",
  category: "logistics",
  logo: "/img/integration/stord.png",
  connectPath: "/setup/connect/stord",
  authType: "password",
}

export const crownPacificFineFoodsConfig: IntegrationConfig = {
  id: "crownpacificfinefoods",
  name: "Crown Pacific Fine Foods",
  category: "distributor",
  // Nat (10/12): brands look for it under Food service, which is how they buy from it.
  classOfTrade: "food_service",
  logo: "/img/integration/crownpacificfinefoods.png",
  connectPath: "/setup/connect/crownpacificfinefoods",
  authType: "email",
}

export const rainforestConfig: IntegrationConfig = {
  id: "rainforest",
  name: "Rainforest",
  category: "distributor",
  classOfTrade: "natural_specialty",
  logo: rainforestLogo,
  connectPath: "/setup/connect/rainforest",
  authType: "email",
}

// Chex runs no vendor portal: every billback arrives as its own email, so like Thrive and SGWS this
// connects over already-connected mailboxes rather than with its own credentials.
export const chexfinerfoodsConfig: IntegrationConfig = {
  id: "chexfinerfoods",
  name: "Chex Finer Foods",
  category: "distributor",
  // Specialty and natural foods into New England grocers, co-ops and natural stores (Big Y, Roche
  // Bros., Whole Foods' North Atlantic region) — a retail distributor, not food service.
  classOfTrade: "natural_specialty",
  logo: chexfinerfoodsLogo,
  description: "Reads Chex billback emails",
  connectPath: "/setup/connect/chexfinerfoods",
  // The settings routes existed but were unreachable from the connections list without this.
  settingsPath: "/settings/connect/chexfinerfoods",
  searchAliases: ["chex", "chex foods", "billback"],
  authType: "email",
}

export const googledriveConfig: IntegrationConfig = {
  id: "googledrive",
  name: "Google Drive",
  category: "logistics",
  logo: "/img/integration/googledrive.png",
  connectPath: "/setup/connect/googledrive",
  authType: "oauth",
}

export const wakefernConfig: IntegrationConfig = {
  id: "wakefern",
  name: "Wakefern",
  category: "retailer",
  logo: "/img/integration/wakefern.png",
  connectPath: "/setup/connect/wakefern",
  settingsPath: "/settings/config/wakefern",
  authType: "password",
}

// AWG is a retailer-owned co-operative wholesaler serving independent grocers — it sits with the
// distributors, not the banners, because the deductions come from the warehouse, not a store.
// Logo is AWG's own 2017 seal, taken from the portal header and resized for the 64px slot.
export const awgConfig: IntegrationConfig = {
  id: "awg",
  name: "AWG",
  category: "distributor",
  classOfTrade: "conventional",
  logo: "/img/integration/awg.png",
  connectPath: "/setup/connect/awg",
  authType: "password",
}

// Roadtex is the refrigerated LTL carrier that moves RJW-warehoused freight to Target and the
// rest — a logistics provider, not a customer. Its portal has no challenge in front of login, so
// this is a plain credential connect.
export const roadtexConfig: IntegrationConfig = {
  id: "roadtex",
  name: "Roadtex",
  logo: "/img/integration/roadtex.svg",
  category: "logistics",
  connectPath: "/setup/connect/roadtex",
  authType: "password",
}

// Thrive Market runs no vendor portal at all — credit memos arrive from their NetSuite and
// remittances from Coupa Pay, both by email — so like GFI this connects over already-connected
// mailboxes rather than with its own credentials.
export const thriveConfig: IntegrationConfig = {
  id: "thrive",
  name: "Thrive Market",
  category: "retailer",
  logo: "/img/integration/email/thrive.webp",
  // A stacked wordmark: taller box, like GFI's stamp.
  logoHeightClass: "h-14",
  description: "Reads Thrive's credit memo and remittance emails, and its Coupa payments",
  connectPath: "/setup/connect/thrive",
  settingsPath: "/settings/connect/thrive",
  searchAliases: ["thrive", "thrive market", "coupa"],
  authType: "email",
}

// Southern Glazer's payments arrive by mail — every ACH mails an "Electronic Remi. Advice" whose PDF
// lists payments and deductions — so like Thrive this connects over already-connected mailboxes.
// Its Supplier Billing portal (billbacks only) is an optional login on the same page, on its own
// `southern-glazer-portal` row. Logo is SGWS's own seal, from their site header.
export const southernGlazerConfig: IntegrationConfig = {
  id: "southern-glazer",
  name: "Southern Glazer's",
  category: "distributor",
  classOfTrade: "alcohol",
  logo: southernGlazerLogo,
  // A round seal, not a wordmark: at the default h-10 it renders 40x40 and the lettering in the
  // gold ring is unreadable next to KeHE's 117x40. Same taller box as GFI's stamp.
  logoHeightClass: "h-14",
  description: "Reads Southern Glazer's remittance advice emails and Supplier Billing portal",
  connectPath: "/setup/connect/southern-glazer",
  settingsPath: "/settings/connect/southern-glazer",
  searchAliases: ["southern glazers", "sgws", "southern glazer's wine & spirits", "biller direct", "supplier billing"],
  authType: "email",
}

// H-E-B has no vendor deduction portal we scrape; every EFT arrives as a "Payment Advice
// Notification" email with the advice attached, read off a connected mailbox. Brands H-E-B does
// not email upload the AP-AR Application's payment export instead (2FA blocks a sync).
export const hebConfig: IntegrationConfig = {
  id: "heb",
  name: "H-E-B",
  category: "retailer",
  logo: "/img/integration/heb.png",
  description: "Reads H-E-B's payment advice emails, or the payment report you upload from its AP-AR portal",
  connectPath: "/setup/connect/heb",
  settingsPath: "/settings/connect/heb",
  searchAliases: ["heb", "h-e-b", "h e b", "payment advice", "ap-ar", "avp"],
  authType: "email",
}

// The long tail of customers that pay by ACH and email the remittance — Big Geyser, Demoula's,
// Merchants Distributors / McLane, Grocery Supply, Hy-Vee, Glazer's, Wright, Yumi, Dynamo, Five
// Below — read by one ingest with a per-payer format registry. No logo asset, so the card is
// text-based.
export const emailRemittanceConfig: IntegrationConfig = {
  id: "email-remittance",
  name: "Remittance emails",
  category: "distributor",
  description: "Reads ACH remittance emails from smaller customers (Big Geyser, Demoula's, MDI, Hy-Vee, Five Below, …)",
  connectPath: "/setup/connect/email-remittance",
  settingsPath: "/settings/connect/email-remittance",
  searchAliases: ["remittance", "big geyser", "demoulas", "market basket", "merchants distributors", "mdi", "grocery supply", "hy-vee", "glazer", "wright beverage", "bellavance", "yumi", "glacier point", "dynamo", "weis", "decrescente", "de crescente", "capitol beverage", "john lenore", "terborg", "quickbooks", "wawa", "five below", "fivebelow", "1616 holdings", "ach"],
  authType: "email",
}

// Costco pays by EFT and emails a "Costco Payment" remittance PDF for every transfer, read off a
// connected mailbox.
export const costcoConfig: IntegrationConfig = {
  id: "costco",
  name: "Costco",
  category: "retailer",
  logo: "/img/integration/email/costco.webp",
  description: "Reads Costco's EFT remittance emails (Costco Canada included)",
  connectPath: "/setup/connect/costco",
  settingsPath: "/settings/connect/costco",
  searchAliases: ["costco", "costco canada", "costco wholesale", "costco payment", "remit_eft"],
  authType: "email",
}

export const tonysFinefooodsConfig: IntegrationConfig = {
  id: "tonysfinefoods",
  name: "Tony's Fine Foods",
  category: "distributor",
  // UNFI's West Coast perishables arm: specialty deli, cheese, protein and bakery, sold mostly to
  // retail and specialty grocers. It has a food service line, but that is not who it mainly serves.
  // Nat (10/12): brands look for it under Food service, which is how they buy from it.
  classOfTrade: "food_service",
  logo: "/img/integration/tonysfinefoods.png",
  connectPath: "/setup/connect/tonysfinefoods",
  searchAliases: ["tonys", "tony's"],
  authType: "password",
}

// Vividly is a trade promotion management system — the brand's own planning tool, not a customer.
// Connecting it reads the promotion plan, product catalog and customer pricing that would otherwise
// be uploaded as CSV exports by hand.
export const vividlyConfig: IntegrationConfig = {
  id: "vividly",
  name: "Vividly",
  logo: "/img/integration/vividly.webp",
  logoHeightClass: "h-8",
  category: "planning",
  description: "Syncs promotions, products and pricing from your Vividly TPM",
  connectPath: "/setup/connect/vividly",
  settingsPath: "/settings/connect/vividly",
  searchAliases: ["vividly", "govividly", "tpm", "trade promotion management"],
  authType: "password",
}

export const vitaminShoppeConfig: IntegrationConfig = {
  id: "vitamin-shoppe",
  name: "The Vitamin Shoppe",
  category: "retailer",
  logo: vitaminShoppeLogo,
  // A stacked mark (the V over the wordmark), not a wide wordmark: at the default h-10 the
  // lettering is unreadable. Same taller box as GFI's stamp and Southern Glazer's seal.
  logoHeightClass: "h-14",
  description: "Reads The Vitamin Shoppe's Wells Fargo remittance advice emails",
  connectPath: "/setup/connect/vitamin-shoppe",
  settingsPath: "/settings/connect/vitamin-shoppe",
  searchAliases: ["vitamin shoppe", "vitaminshoppe", "vitamin shop", "wells fargo remittance"],
  authType: "email",
}

// WinCo Foods buys direct into its own DCs and runs no vendor deduction portal; every EFT arrives
// as an emailed payment breakdown PDF.
export const wincoConfig: IntegrationConfig = {
  id: "winco",
  name: "WinCo Foods",
  category: "retailer",
  logo: "/img/integration/email/winco.webp",
  logoHeightClass: "h-12",
  description: "Reads WinCo's EFT payment notification emails and the backup WinCo mails",
  connectPath: "/setup/connect/winco",
  settingsPath: "/settings/connect/winco",
  searchAliases: ["winco", "winco foods", "wincofoods", "eft payment notification"],
  authType: "email",
}

// Muscle Foods USA is a division of EurPac Service, Inc. — a distributor into GNC, the military
// exchanges and independents.
export const muscleFoodsConfig: IntegrationConfig = {
  id: "muscle-foods",
  name: "Muscle Foods USA",
  logo: "/img/integration/musclefoods.webp",
  logoHeightClass: "h-14",
  category: "distributor",
  // Sports nutrition wholesale to nutrition stores, gyms, health food stores, grocery chains and
  // c-stores, plus the military exchanges through EurPac. Nothing here is food service; specialty
  // retail is the closest of the four classes.
  // Nat (10/12): brands look for it under Convenience, which is how they buy from it.
  classOfTrade: "convenience",
  description: "Reads Muscle Foods USA's ACH payment notification emails",
  connectPath: "/setup/connect/muscle-foods",
  settingsPath: "/settings/connect/muscle-foods",
  searchAliases: ["muscle foods", "musclefoods", "eurpac", "eurpac service", "payment notification"],
  authType: "email",
}

// Core-Mark (Performance Food Group) — the convenience-store distributor. Read off its SAP payment
// advice PDFs and the daily backup bundle.
export const coreMarkConfig: IntegrationConfig = {
  id: "core-mark",
  name: "Core-Mark",
  logo: "/img/integration/coremark.webp",
  category: "distributor",
  classOfTrade: "convenience",
  description: "Reads Core-Mark's payment advice PDFs and attaches each debit's backup from the daily bundle",
  connectPath: "/setup/connect/core-mark",
  settingsPath: "/settings/connect/core-mark",
  searchAliases: ["core-mark", "coremark", "core mark", "eby brown", "eby-brown", "pfg", "performance food group", "payment advice note"],
  authType: "email",
}

// BILL (formerly Bill.com) is a payment processor, not a customer — the rows each notification
// books are filed against the payer it names. Categorised as "accounting" because that is what a
// brand is doing here: reading cash application off its own AP mail.
export const billConfig: IntegrationConfig = {
  id: "bill",
  name: "BILL",
  logo: "/img/integration/bill.svg",
  category: "accounting",
  description: "Reads \"customer has paid you\" deposit emails from BILL (Bill.com)",
  connectPath: "/setup/connect/bill",
  settingsPath: "/settings/connect/bill",
  searchAliases: ["bill", "bill.com", "billcom", "has paid you", "payment notification"],
  authType: "email",
}

// Albertsons Companies (Safeway, Vons, Jewel-Osco, ACME, Shaw's, Randalls, Tom Thumb, …), whose AP
// runs as Safeway Inc. Read off the "ELECTRONIC REMITTANCE ADVICE" emails, plus the Apex vendor
// portal's Cash Forecast export for the history that predates the brand's move to EFT.
export const albertsonsConfig: IntegrationConfig = {
  id: "albertsons",
  name: "Albertsons",
  category: "retailer",
  logo: "/img/integration/email/albertsons.webp",
  // A long wordmark (6.4:1): at the default h-10 it is 257px wide and spills out of the card.
  logoHeightClass: "h-8",
  description: "Reads Albertsons' remittance advice emails, or the Cash Forecast report you export from its Apex portal",
  connectPath: "/setup/connect/albertsons",
  settingsPath: "/settings/connect/albertsons",
  searchAliases: ["albertsons", "safeway", "vons", "jewel-osco", "jewel osco", "acme", "shaw's", "shaws", "randalls", "tom thumb", "pavilions", "star market", "carrs", "haggen", "apex", "apexportal", "cash forecast", "electronic remittance advice"],
  authType: "email",
}

// McLane and Vistar have no connection of their own: both are read off a connected mailbox. The
// cards exist so a c-store brand finds its distributor by name, and they open the remittance-email
// connect (`connectsVia`), which is where that mailbox is chosen. McLane's Oracle payment advice
// and `Deduction Support` mail are read automatically. Vistar's (PFG) mail is not yet: its shortage
// deduction emails are booked by staff from the admin ingest preview, and there is no Vistar
// remittance format in the reader.
export const mclaneConfig: IntegrationConfig = {
  id: "mclane",
  name: "McLane",
  logo: "/img/integration/email/mclane.webp",
  category: "distributor",
  classOfTrade: "convenience",
  connectsVia: "email-remittance",
  description: "Reads McLane's payment advice and deduction support emails",
  searchAliases: ["mclane", "mclaneco", "circle k", "deduction support"],
  authType: "email",
}

export const vistarConfig: IntegrationConfig = {
  id: "vistar",
  name: "Vistar",
  logo: "/img/integration/vistar.webp",
  logoHeightClass: "h-12",
  category: "distributor",
  // PFG's Vistar sells candy, snacks and beverages to vending and office-coffee operators, theaters,
  // hospitality and impulse retail. PFG reports it apart from its Convenience segment (Core-Mark),
  // so "convenience" is the nearest of the four classes rather than an exact fit.
  classOfTrade: "convenience",
  connectsVia: "email-remittance",
  description: "Reads Vistar's deduction emails from a connected mailbox",
  searchAliases: ["vistar", "pfg", "performance food group", "pfgc"],
  authType: "email",
}

// SPINS: syndicated scan data, read from the brand's Satori extracts after every SPINS release.
// Logo is SPINS' own wordmark, from spins.com.
export const spinsConfig: IntegrationConfig = {
  id: "spins",
  name: "SPINS",
  category: "data",
  logo: "/img/integration/spins.png",
  description: "Your SPINS sales data from Satori, refreshed after every release",
  connectPath: "/setup/connect/spins",
  settingsPath: "/settings/connect/spins",
  searchAliases: ["spins", "satori", "syndicated", "scan data", "pos data", "spinsscan"],
  authType: "password",
}

export const allIntegrations: IntegrationConfig[] = [
  keheConfig,
  unfiConfig,
  targetConfig,
  walmartConfig,
  walmartHighRadiusConfig,
  hyveeConfig,
  amazonConfig,
  quickbooksConfig,
  xeroConfig,
  sageIntacctConfig,
  netsuiteConfig,
  aholdConfig,
  svHarborConfig,
  candsConfig,
  spsConfig,
  krogerConfig,
  bozzutosConfig,
  dotFoodsConfig,
  dollarGeneralConfig,
  crstlConfig,
  freightzyConfig,
  stordConfig,
  crownPacificFineFoodsConfig,
  rainforestConfig,
  chexfinerfoodsConfig,
  googledriveConfig,
  bolEmailSyncConfig,
  wakefernConfig,
  awgConfig,
  roadtexConfig,
  thriveConfig,
  southernGlazerConfig,
  vitaminShoppeConfig,
  wincoConfig,
  muscleFoodsConfig,
  coreMarkConfig,
  mclaneConfig,
  vistarConfig,
  billConfig,
  hebConfig,
  albertsonsConfig,
  costcoConfig,
  emailRemittanceConfig,
  tonysFinefooodsConfig,
  gfiConfig,
  vividlyConfig,
  spinsConfig,
]

export const distributorIntegrations = allIntegrations.filter(i => i.category === "distributor")
export const planningIntegrations = allIntegrations.filter(i => i.category === "planning")
export const dataIntegrations = allIntegrations.filter(i => i.category === "data")
export const retailerIntegrations = allIntegrations.filter(i => i.category === "retailer")
export const accountingIntegrations = allIntegrations.filter(i => i.category === "accounting")
export const logisticsIntegrations = allIntegrations.filter(i => i.category === "logistics")
